import * as THREE from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import { uniform, uv, wgslFn } from 'three/tsl';
import type { RendererMode } from './Renderer';

export interface PlasmaBeamControls {
  setHue(hueDeg: number): void;
  setIntensity(value: number): void;
  advance(dt: number): void;
}

export type PlasmaBeamRuntimeMaterial = THREE.Material & PlasmaBeamControls;

const makeNodeUniform = uniform as any;
const makeNodeUv = uv as any;
const makeWgslFn = wgslFn as any;

interface WebGPUPlasmaUniforms {
  uTime: { value: number };
  uIntensity: { value: number };
  uCoreColor: { value: THREE.Vector3 };
}

const VERT = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = `
  uniform float uTime;
  uniform float uIntensity;
  uniform vec3 uCoreColor;
  varying vec2 vUv;

  // Compact 3D hash noise.
  float hash(vec3 p) {
    p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
    p *= 17.0;
    return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
  }

  float noise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n000 = hash(i + vec3(0.0, 0.0, 0.0));
    float n100 = hash(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash(i + vec3(1.0, 1.0, 1.0));
    float nx00 = mix(n000, n100, f.x);
    float nx10 = mix(n010, n110, f.x);
    float nx01 = mix(n001, n101, f.x);
    float nx11 = mix(n011, n111, f.x);
    float nxy0 = mix(nx00, nx10, f.y);
    float nxy1 = mix(nx01, nx11, f.y);
    return mix(nxy0, nxy1, f.z);
  }

  void main() {
    if (uIntensity <= 0.001) discard;

    // Two scrolling layers along the beam length (vUv.x = 0..1).
    float fast = noise(vec3(vUv.x * 19.0 - uTime * 4.8, vUv.y * 7.0, 0.0));
    float slow = noise(vec3(vUv.x * 5.0 - uTime * 1.5, vUv.y * 2.0, 1.7));
    float turb = fast * 0.5 + slow * 0.45;

    // Radial falloff across the tube circumference (vUv.y = 0..1).
    float radial = abs(vUv.y - 0.5) * 2.0;
    float core = 1.0 - smoothstep(0.0, 0.85, radial);
    core = pow(core, 1.8);

    // Traveling knots keep acceleration lively without making the whole beam brighter.
    float knotA = 1.0 - smoothstep(0.0, 0.11, abs(fract(vUv.x * 3.1 - uTime * 1.35) - 0.5));
    float knotB = 1.0 - smoothstep(0.0, 0.08, abs(fract(vUv.x * 5.4 + uTime * 0.95) - 0.5));
    float pulse = pow(max(knotA, knotB * 0.68), 2.0);

    // Endpoint flare: just enough contact glow, not a large purple bloom ball.
    float ends = pow(abs(vUv.x - 0.5) * 2.0, 2.2);
    float flicker = 0.9 + 0.1 * sin(uTime * 22.0 + vUv.x * 24.0);

    vec3 col = uCoreColor * core * (0.5 + turb * 0.42 + pulse * 0.58) * flicker;
    col += uCoreColor * 0.1 * ends * core;

    float alpha = core * (0.42 + turb * 0.24 + pulse * 0.42) * uIntensity;
    gl_FragColor = vec4(col * uIntensity, clamp(alpha, 0.0, 1.0));
  }
`;

export class PlasmaBeamMaterial extends THREE.ShaderMaterial {
  constructor(initialHueDeg = 290, initialIntensity = 1) {
    super({
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: initialIntensity },
        uCoreColor: { value: hueToHdr(initialHueDeg) },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
  }

  setHue(hueDeg: number): void {
    (this.uniforms.uCoreColor.value as THREE.Vector3).copy(hueToHdr(hueDeg));
  }

  setIntensity(value: number): void {
    this.uniforms.uIntensity.value = THREE.MathUtils.clamp(value, 0, 2);
  }

  advance(dt: number): void {
    this.uniforms.uTime.value += dt;
  }
}

const plasmaHashNode = makeWgslFn(`
fn plasma_hash(p: vec3<f32>) -> f32 {
  var q = fract(p * 0.3183099 + vec3<f32>(0.1, 0.2, 0.3));
  q = q * 17.0;
  return fract(q.x * q.y * q.z * (q.x + q.y + q.z));
}
`);

const plasmaNoiseNode = makeWgslFn(`
fn plasma_noise(p: vec3<f32>) -> f32 {
  let i = floor(p);
  let f0 = fract(p);
  let f = f0 * f0 * (vec3<f32>(3.0, 3.0, 3.0) - 2.0 * f0);
  let n000 = plasma_hash(i + vec3<f32>(0.0, 0.0, 0.0));
  let n100 = plasma_hash(i + vec3<f32>(1.0, 0.0, 0.0));
  let n010 = plasma_hash(i + vec3<f32>(0.0, 1.0, 0.0));
  let n110 = plasma_hash(i + vec3<f32>(1.0, 1.0, 0.0));
  let n001 = plasma_hash(i + vec3<f32>(0.0, 0.0, 1.0));
  let n101 = plasma_hash(i + vec3<f32>(1.0, 0.0, 1.0));
  let n011 = plasma_hash(i + vec3<f32>(0.0, 1.0, 1.0));
  let n111 = plasma_hash(i + vec3<f32>(1.0, 1.0, 1.0));
  let nx00 = mix(n000, n100, f.x);
  let nx10 = mix(n010, n110, f.x);
  let nx01 = mix(n001, n101, f.x);
  let nx11 = mix(n011, n111, f.x);
  let nxy0 = mix(nx00, nx10, f.y);
  let nxy1 = mix(nx01, nx11, f.y);
  return mix(nxy0, nxy1, f.z);
}
`, [plasmaHashNode]);

const plasmaBeamNode = makeWgslFn(`
fn plasma_beam(
  texCoord: vec2<f32>,
  time: f32,
  intensity: f32,
  coreColor: vec3<f32>
) -> vec4<f32> {
  if (intensity <= 0.001) {
    return vec4<f32>(0.0, 0.0, 0.0, 0.0);
  }

  let fast = plasma_noise(vec3<f32>(texCoord.x * 19.0 - time * 4.8, texCoord.y * 7.0, 0.0));
  let slow = plasma_noise(vec3<f32>(texCoord.x * 5.0 - time * 1.5, texCoord.y * 2.0, 1.7));
  let turb = fast * 0.5 + slow * 0.45;
  let radial = abs(texCoord.y - 0.5) * 2.0;
  let core = pow(1.0 - smoothstep(0.0, 0.85, radial), 1.8);
  let knotA = 1.0 - smoothstep(0.0, 0.11, abs(fract(texCoord.x * 3.1 - time * 1.35) - 0.5));
  let knotB = 1.0 - smoothstep(0.0, 0.08, abs(fract(texCoord.x * 5.4 + time * 0.95) - 0.5));
  let pulse = pow(max(knotA, knotB * 0.68), 2.0);
  let ends = pow(abs(texCoord.x - 0.5) * 2.0, 2.2);
  let flicker = 0.9 + 0.1 * sin(time * 22.0 + texCoord.x * 24.0);
  var col = coreColor * core * (0.5 + turb * 0.42 + pulse * 0.58) * flicker;
  col = col + coreColor * 0.1 * ends * core;
  let alpha = core * (0.42 + turb * 0.24 + pulse * 0.42) * intensity;
  return vec4<f32>(col * intensity, clamp(alpha, 0.0, 1.0));
}
`, [plasmaNoiseNode]);

class WebGPUPlasmaBeamMaterial extends MeshBasicNodeMaterial implements PlasmaBeamControls {
  private readonly uniforms: WebGPUPlasmaUniforms;

  constructor(initialHueDeg = 290, initialIntensity = 1) {
    super({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    this.uniforms = {
      uTime: makeNodeUniform(0),
      uIntensity: makeNodeUniform(THREE.MathUtils.clamp(initialIntensity, 0, 2)),
      uCoreColor: makeNodeUniform(hueToHdr(initialHueDeg)),
    };
    const beam = plasmaBeamNode({
      texCoord: makeNodeUv(),
      time: this.uniforms.uTime,
      intensity: this.uniforms.uIntensity,
      coreColor: this.uniforms.uCoreColor,
    });
    this.colorNode = beam.rgb;
    this.opacityNode = beam.a;
  }

  setHue(hueDeg: number): void {
    this.uniforms.uCoreColor.value.copy(hueToHdr(hueDeg));
  }

  setIntensity(value: number): void {
    this.uniforms.uIntensity.value = THREE.MathUtils.clamp(value, 0, 2);
  }

  advance(dt: number): void {
    this.uniforms.uTime.value += dt;
  }
}

export function createPlasmaBeamMaterial(
  rendererMode: RendererMode,
  initialHueDeg = 290,
  initialIntensity = 1,
): PlasmaBeamRuntimeMaterial {
  return rendererMode === 'webgpu'
    ? new WebGPUPlasmaBeamMaterial(initialHueDeg, initialIntensity)
    : new PlasmaBeamMaterial(initialHueDeg, initialIntensity);
}

/**
 * Convert a hue angle to an HDR color vec3 with brightness > 1.0 so the beam
 * triggers bloom (or just reads as bright with additive blending).
 */
function hueToHdr(hueDeg: number): THREE.Vector3 {
  const h = THREE.MathUtils.euclideanModulo(hueDeg, 360) / 360;
  const c = new THREE.Color().setHSL(h, 0.9, 0.6);
  // Push into HDR territory so additive blending saturates and bloom catches.
  return new THREE.Vector3(c.r * 2.8, c.g * 2.25, c.b * 3.55);
}
